/**
 * Shared plan-entitlement store + `useEntitlement()` hook (L-entitlements).
 *
 * One shell-wide source of truth for the tenant's plan limits + boolean feature
 * flags (lifted off `GET /api/v1/subscription/me`), so any surface can gate a
 * whole feature (`<PlanGate>`) or a create action without each re-fetching. The
 * backend enforces the same flags/plafonds authoritatively — this only drives
 * the UX (hide/disable + upgrade CTA).
 */
import { useEffect } from 'react';
import { create } from 'zustand';

import {
  getPlanEntitlements,
  planHasFeature,
  planLimitFor,
  type PlanEntitlements,
} from '@/lib/api/entitlements-plan';
import { PLAN_REQUIRED_EVENT, type PlanRequired } from '@/lib/api/plan-gate';

/** Fire-and-forget refresh (e.g. after a plan change). */
export const PLAN_ENTITLEMENTS_UPDATED_EVENT = 'psn:plan-entitlements-updated';

export function requestEntitlementsRefresh(): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent(PLAN_ENTITLEMENTS_UPDATED_EVENT));
  } catch {
    /* ignore */
  }
}

interface EntitlementsState {
  entitlements: PlanEntitlements | null;
  loading: boolean;
  loaded: boolean;
  _inflight: boolean;
  /** Features the SERVER refused with 403 feature_not_in_plan (S8) — authoritative
   *  over the cached plan document until the next forced refresh. */
  denied: Record<string, PlanRequired>;
  markDenied: (info: PlanRequired) => void;
  refresh: (opts?: { force?: boolean }) => Promise<void>;
}

export const useEntitlementsStore = create<EntitlementsState>((set, get) => ({
  entitlements: null,
  loading: false,
  loaded: false,
  _inflight: false,
  denied: {},

  markDenied: (info) => {
    if (get().denied[info.feature]) return;
    set({ denied: { ...get().denied, [info.feature]: info } });
  },

  refresh: async (opts) => {
    if (get()._inflight && !opts?.force) return;
    if (get().loaded && !opts?.force) return;
    set({ _inflight: true, loading: !get().loaded });
    try {
      const entitlements = await getPlanEntitlements();
      // A forced refresh (e.g. after an upgrade) re-trusts the plan document.
      set(opts?.force ? { entitlements, loaded: true, denied: {} } : { entitlements, loaded: true });
    } catch {
      // Degrade OPEN: if entitlements can't be read, don't hard-block the UI —
      // the backend still enforces. `hasFeature` then returns false-ish, so
      // callers should treat `loaded=false` as "unknown" (see useEntitlement).
      set({ loaded: true });
    } finally {
      set({ _inflight: false, loading: false });
    }
  },
}));

// S8 — the API client fires PLAN_REQUIRED_EVENT on any 403 feature_not_in_plan;
// record it once at module scope so every mounted <PlanGate> reacts.
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener(PLAN_REQUIRED_EVENT, (e: Event) => {
    const detail = (e as CustomEvent<PlanRequired>).detail;
    if (detail && detail.feature) useEntitlementsStore.getState().markDenied(detail);
  });
}

export interface EntitlementApi {
  entitlements: PlanEntitlements | null;
  loading: boolean;
  loaded: boolean;
  planCode: string | null;
  upgradeUrl: string;
  /** The caller is the platform superadmin: every feature unlocked, every limit unlimited. */
  isSuperadmin: boolean;
  /** True when the tenant's OWN plan includes `key` (ignores the superadmin override). */
  planIncludes: (key: string) => boolean;
  /** True when the plan includes `key`. While entitlements are still loading /
   *  unknown this returns `fallback` (default true) so the UI never flashes a
   *  gate before data arrives. */
  hasFeature: (key: string, fallback?: boolean) => boolean;
  /** True when the server refused `key` with 403 feature_not_in_plan this session. */
  isDenied: (key: string) => boolean;
  /** The plafond for `key` (`-1` = unlimited), or null when uncapped/unknown. */
  limitFor: (key: string) => number | null;
  refresh: (opts?: { force?: boolean }) => Promise<void>;
}

/** Read the tenant's plan entitlements (auto-loads once on first mount). */
/** Non-reactive read: is the cached caller the platform superadmin? (false until loaded) */
export function isSuperadminCached(): boolean {
  return useEntitlementsStore.getState().entitlements?.superadmin === true;
}

export function useEntitlement(): EntitlementApi {
  const { entitlements, loading, loaded, refresh, denied } = useEntitlementsStore();

  useEffect(() => {
    void refresh();
    const onUpdated = () => void refresh({ force: true });
    window.addEventListener(PLAN_ENTITLEMENTS_UPDATED_EVENT, onUpdated);
    return () => window.removeEventListener(PLAN_ENTITLEMENTS_UPDATED_EVENT, onUpdated);
  }, [refresh]);

  const isSuperadmin = entitlements?.superadmin === true;
  return {
    entitlements,
    loading,
    loaded,
    planCode: entitlements?.planCode ?? null,
    upgradeUrl: entitlements?.upgradeUrl ?? '/settings/subscription',
    isSuperadmin,
    planIncludes: (key: string) =>
      Boolean((entitlements?.planFeatures ?? entitlements?.features ?? {})[key]),
    // The platform superadmin is never plan-gated (the backend bypasses every
    // plan gate for them too), so a superadmin always has the feature.
    hasFeature: (key: string, fallback = true) =>
      isSuperadmin
        ? true
        : denied[key]
          ? false
          : entitlements
            ? planHasFeature(entitlements, key)
            : fallback,
    isDenied: (key: string) => (isSuperadmin ? false : Boolean(denied[key])),
    limitFor: (key: string) =>
      isSuperadmin ? -1 : entitlements ? planLimitFor(entitlements, key) : null,
    refresh,
  };
}
